/**
 * Page numbers on an A4 print view.
 *
 * §11.6 asks for "the A4 print view", and a kitchen document that runs to two
 * sheets and does not say which is which is a document somebody loses half
 * of. CSS has an answer — `@page { @bottom-center { content: counter(page) } }`
 * — and no browser engine in use here implements it: Chrome and Firefox both
 * drop the margin boxes, and the page numbers you get from a browser's own
 * print dialogue are the browser's, not the document's, and are off by
 * default.
 *
 * So the document paginates itself. It knows its own rows, so it chunks them
 * into sheets and prints *2 / 3. oldal* on each one. The arithmetic is here,
 * away from the component, because it is the part worth testing: an empty
 * document is one page and not none, and the first sheet holds fewer rows than
 * the rest because the header is on it.
 */

export interface Sheet<T> {
  /** 1-based. */
  page: number
  rows: T[]
}

export interface Paginated<T> {
  sheets: Sheet<T>[]
  pages: number
}

/**
 * `firstPageRows` is what fits under the header band and the order header;
 * `perPage` is a full sheet. Both are row counts measured against the A4
 * sheet at Raleway 10 pt — see `KITCHEN_ROWS_FIRST` and `KITCHEN_ROWS_REST`.
 */
export function paginate<T>(rows: T[], firstPageRows: number, perPage: number): Paginated<T> {
  const first = Math.max(1, Math.floor(firstPageRows))
  const rest = Math.max(1, Math.floor(perPage))
  const sheets: Sheet<T>[] = []

  // An empty document is one page. A production order with nothing on it is
  // still a thing the kitchen has to be handed and told is empty.
  if (rows.length === 0) return { sheets: [{ page: 1, rows: [] }], pages: 1 }

  let index = 0
  while (index < rows.length) {
    const take = sheets.length === 0 ? first : rest
    sheets.push({ page: sheets.length + 1, rows: rows.slice(index, index + take) })
    index += take
  }
  return { sheets, pages: sheets.length }
}

/** *3. oldal / 4* — the Hungarian form, with the ordinal full stop (§5.12). */
export function pageLabel(page: number, pages: number): string {
  return `${page}. oldal / ${pages}`
}
